import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
  HiOutlineClipboardDocumentList,
  HiOutlineMagnifyingGlass,
  HiOutlineCheckCircle,
  HiOutlineChevronRight,
  HiOutlineExclamationTriangle,
  HiOutlineInformationCircle,
  HiOutlinePencilSquare,
  HiOutlineClock,
  HiOutlineCalendarDays,
  HiOutlinePhoto,
  HiOutlineXMark,
  HiOutlineTrash,
  HiOutlineWrench,
  HiOutlineArrowLeft,
  HiOutlinePower,
} from 'react-icons/hi2';
import { authService, uploadService, plantService, lineService } from '../../../services';
import pmStandardService from '../../../services/pmStandardService';
import sparePartsService from '../../../services/sparePartsService';
import { toast } from 'react-toastify';
import { SEMANTIC_SUCCESS_MID } from '../../../theme/chartColors';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import './OperatorPmTasks.css';

const STATUS_CONFIG = {
  Overdue: { label: 'Overdue', className: 'status-overdue', icon: HiOutlineExclamationTriangle },
  Due: { label: 'Due Today', className: 'status-due', icon: HiOutlineClock },
  Scheduled: { label: 'Scheduled', className: 'status-scheduled', icon: HiOutlineCalendarDays },
  Completed: { label: 'Completed', className: 'status-completed', icon: HiOutlineCheckCircle },
};

const OperatorPmTasks = () => {
  const tenantId = authService.getTenantId();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const paramStandardId = searchParams.get('standardId');
  const paramDate = searchParams.get('date');

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [frequencyFilter, setFrequencyFilter] = useState('');
  const [plantFilter, setPlantFilter] = useState('');
  const [lineFilter, setLineFilter] = useState('');
  const [plants, setPlants] = useState([]);
  const [lines, setLines] = useState([]);
  const [dateFilterMode, setDateFilterMode] = useState('all'); // all, overdue, today, upcoming, specific
  const [specificDate, setSpecificDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [availableParts, setAvailableParts] = useState([]);
  const [selectedTask, setSelectedTask] = useState(() => location.state?.task || null);
  const toastIdRef = React.useRef(null);
  const scrollRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [finding, setFinding] = useState(() => {
    if (location.state?.task?.status === 'Completed') return location.state.task.finding || '';
    return '';
  });
  const [actionTaken, setActionTaken] = useState(() => {
    if (location.state?.task?.status === 'Completed') return location.state.task.actionTaken || '';
    return '';
  });
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidencePreview, setEvidencePreview] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [minutes, setMinutes] = useState('');
  const [isMachineShutdownRequired, setIsMachineShutdownRequired] = useState(false);
  const [isEditing, setIsEditing] = useState(() => !!location.state?.edit);
  const [submitting, setSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const initialLoadRef = useRef(true);

  // Spare parts state
  const [consumedParts, setConsumedParts] = useState([]);
  const [selectedPartId, setSelectedPartId] = useState('');
  const [partQuantity, setPartQuantity] = useState('');

  const loadTasks = useCallback(async () => {
    try {
      // Duplication check: only show one toast if we already have tasks
      if (tasks.length > 0 && !toastIdRef.current) {
        toastIdRef.current = toast.loading('Refreshing tasks...');
      }
      
      setLoading(true);
      const data = await pmStandardService.getMyTasks({
        plantId: plantFilter || null,
        lineId: lineFilter || null
      });
      setTasks(data || []);
      return data;
    } catch (err) {
      setError('Failed to load tasks. Please try again.');
      toast.error('Error loading PM tasks');
    } finally {
      setLoading(false);
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
    }
  }, [plantFilter, lineFilter, tasks.length]);

  useEffect(() => {
    if (tenantId) {
      loadTasks();
    }
  }, [tenantId, loadTasks]);

  useEffect(() => {
    if (tenantId) {
      loadPlants();
      loadSpareParts();

      // Load history immediately if task was passed via state
      if (location.state?.task) {
        loadHistory(location.state.task.standardId);
      }
    }
  }, [tenantId, location.state]);

  const loadPlants = async () => {
    try {
      const data = await plantService.getAllPlants();
      setPlants(data || []);

      // Auto-select plant ONLY for single-plant users
      if (data?.length === 1) {
        setPlantFilter(data[0].plantId.toString());
      }
    } catch (err) {
      console.error('Error loading plants');
    }
  };

  const loadLines = useCallback(async (plantId) => {
    try {
      const data = await lineService.getAllLines(plantId);
      setLines(data || []);
    } catch (err) {
      console.error('Error loading lines');
    }
  }, []);

  useEffect(() => {
    if (plantFilter) {
      loadLines(plantFilter);
      setLineFilter('');
    } else {
      setLines([]);
      setLineFilter('');
    }
  }, [plantFilter, loadLines]);

  useEffect(() => {
    if (tasks.length > 0 && !selectedTask && paramStandardId) {
      const stdId = parseInt(paramStandardId);
      const task = tasks.find(t =>
        t.standardId === stdId &&
        (!paramDate || t.dueDate.startsWith(paramDate))
      );
      if (task) {
        handleSelectTask(task);
      }
    }
  }, [tasks, paramStandardId, paramDate, selectedTask]);

  const loadSpareParts = async () => {
    try {
      const data = await sparePartsService.getParts();
      setAvailableParts(data || []);
    } catch (err) {
      console.error('Failed to load spare parts');
    }
  };

  const loadHistory = async (standardId) => {
    try {
      setLoadingHistory(true);
      const data = await pmStandardService.getHistory(standardId);
      setHistory(data || []);
    } catch (err) {
      console.error('Error loading history', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSelectTask = (task) => {
    setSelectedTask(task);
    if (task.status === 'Completed') {
      setFinding(task.finding || '');
      setActionTaken(task.actionTaken || '');
      setStartTime(task.startTime ? new Date(task.startTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '');
      setEndTime(task.endTime ? new Date(task.endTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '');
      setMinutes(task.minutes || '');
      setIsMachineShutdownRequired(task.isMachineShutdownRequired || false);
    } else {
      setFinding('');
      setActionTaken('');
      setStartTime('');
      setEndTime('');
      setMinutes('');
      setIsMachineShutdownRequired(false);
    }
    if (initialLoadRef.current && location.state?.edit) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
    initialLoadRef.current = false;

    setEvidenceFile(null);
    setEvidencePreview(null);
    setConsumedParts([]);
    loadHistory(task.standardId);
  };

  const handleAddPart = () => {
    if (!selectedPartId || !partQuantity || partQuantity <= 0) return;
    const part = availableParts.find(p => p.sparePartId === parseInt(selectedPartId));
    if (!part) return;

    setConsumedParts(prev => [
      ...prev,
      { sparePartId: part.sparePartId, partName: part.partName, quantity: parseFloat(partQuantity) }
    ]);
    setSelectedPartId('');
    setPartQuantity('');
  };

  const removeConsumedPart = (idx) => {
    setConsumedParts(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!finding.trim()) {
      toast.warning('Please enter findings');
      return;
    }

    try {
      setSubmitting(true);

      let uploadedUrl = null;
      if (evidenceFile) {
        const uploadRes = await uploadService.uploadFile(evidenceFile);
        uploadedUrl = uploadRes.url;
      }

      await pmStandardService.logTransaction({
        standardId: selectedTask.standardId,
        finding: finding.trim(),
        actionTaken: actionTaken.trim(),
        evidenceUrl: uploadedUrl,
        transactionDate: selectedTask.dueDate,
        startTime: startTime ? new Date(`${selectedTask.dueDate.split('T')[0]}T${startTime}`).toISOString() : null,
        endTime: endTime ? new Date(`${selectedTask.dueDate.split('T')[0]}T${endTime}`).toISOString() : null,
        minutes: minutes ? parseInt(minutes) : null,
        isMachineShutdownRequired: isMachineShutdownRequired,
        spareParts: consumedParts.length > 0 ? consumedParts : null,
      });
      toast.success(isEditing ? 'Task updated successfully!' : 'Task completed successfully!');
      setIsEditing(false);
      const freshTasks = await loadTasks();

      // Keep completed task selected so user sees the success state
      const updatedTask = freshTasks?.find(t =>
        t.standardId === selectedTask.standardId &&
        t.dueDate === selectedTask.dueDate
      );
      if (updatedTask) {
        setSelectedTask(updatedTask);
      } else {
        setSelectedTask(null);
      }

      setEvidenceFile(null);
      setEvidencePreview(null);
      setConsumedParts([]);
    } catch (err) {
      toast.error('Failed to save update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEvidenceFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setEvidencePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removeFile = (e) => {
    e.stopPropagation();
    setEvidenceFile(null);
    setEvidencePreview(null);
  };

  // Compute summary counts
  const overdueCnt = tasks.filter(t => t.status === 'Overdue').length;
  const dueCnt = tasks.filter(t => t.status === 'Due').length;
  const upcomingCnt = tasks.filter(t => t.status === 'Scheduled').length;
  const completedCnt = tasks.filter(t => t.status === 'Completed').length;

  const filteredTasks = tasks.filter(t => {
    const matchesSearch =
      t.standardName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.standardCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.machineName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || t.status === statusFilter;
    const matchesFrequency = !frequencyFilter || t.frequency === frequencyFilter;

    let matchesDate = true;
    const todayStr = new Date().toISOString().split('T')[0];
    const taskDateStr = t.dueDate?.split('T')[0];

    if (dateFilterMode === 'overdue') {
      matchesDate = t.status === 'Overdue';
    } else if (dateFilterMode === 'today') {
      matchesDate = taskDateStr === todayStr;
    } else if (dateFilterMode === 'upcoming') {
      matchesDate = taskDateStr > todayStr;
    } else if (dateFilterMode === 'specific') {
      matchesDate = taskDateStr === specificDate;
    }

    return matchesSearch && matchesStatus && matchesFrequency && matchesDate;
  });

  // Unique frequencies and dates for filters
  const uniqueFrequencies = [...new Set(tasks.map(t => t.frequency).filter(Boolean))].sort();
  const uniqueDates = [...new Set(tasks.map(t => t.dueDate?.split('T')[0]).filter(Boolean))]
    .filter(date => date !== new Date().toISOString().split('T')[0])
    .sort();

  const handleEditTask = () => {
    setIsEditing(true);
    setFinding(selectedTask.finding || '');
    setActionTaken(selectedTask.actionTaken || '');
    setStartTime(selectedTask.startTime ? new Date(selectedTask.startTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '');
    setEndTime(selectedTask.endTime ? new Date(selectedTask.endTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '');
    setMinutes(selectedTask.minutes || '');
    setIsMachineShutdownRequired(selectedTask.isMachineShutdownRequired || false);
    setConsumedParts(selectedTask.sparePartsJson ? JSON.parse(selectedTask.sparePartsJson).map(p => ({
      sparePartId: p.SparePartId || p.sparePartId,
      partName: p.PartName || p.partName,
      quantity: p.Quantity || p.quantity
    })) : []);
    // Note: evidenceFile and evidencePreview are not reloaded for editing, user must re-upload if needed.
    setEvidenceFile(null);
    setEvidencePreview(null);
  };

  // Returns 'today', 'past', or 'future' relative to the current date
  const getTaskDateStatus = (dateStr) => {
    if (!dateStr) return 'past';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const taskDate = new Date(dateStr);
    taskDate.setHours(0, 0, 0, 0);
    const diff = taskDate.getTime() - today.getTime();
    if (diff === 0) return 'today';
    if (diff < 0) return 'past';
    return 'future';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const taskDate = new Date(d);
    taskDate.setHours(0, 0, 0, 0);

    if (taskDate.getTime() === today.getTime()) return 'Today';

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (taskDate.getTime() === yesterday.getTime()) return 'Yesterday';

    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading && tasks.length === 0) {
    return (
      <div className="operator-tasks-container">
        <PageLoading message="Loading your tasks..." />
      </div>
    );
  }

  const taskDateStatus = selectedTask ? getTaskDateStatus(selectedTask.dueDate) : null;

  return (
    <div className="pagecontainer">
      <div className="page-header">
        <div className='header-contain'>
          <h1>My PM Tasks</h1>
          <p>Maintenance Checks & Standards</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className={`summary-card backnone overdue ${statusFilter === 'Overdue' ? 'active' : ''}`} onClick={() => setStatusFilter(statusFilter === 'Overdue' ? '' : 'Overdue')}>
          <HiOutlineExclamationTriangle size={22} />
          <div className="summary-info">
            <span className="summary-count">{overdueCnt}</span>
            <span className="summary-label">Overdue</span>
          </div>
        </div>
        <div className={`summary-card backnone due ${statusFilter === 'Due' ? 'active' : ''}`} onClick={() => setStatusFilter(statusFilter === 'Due' ? '' : 'Due')}>
          <HiOutlineClock size={22} />
          <div className="summary-info">
            <span className="summary-count">{dueCnt}</span>
            <span className="summary-label">Due Today</span>
          </div>
        </div>
        <div className={`summary-card backnone upcoming ${statusFilter === 'Scheduled' ? 'active' : ''}`} onClick={() => setStatusFilter(statusFilter === 'Scheduled' ? '' : 'Scheduled')}>
          <HiOutlineCalendarDays size={22} />
          <div className="summary-info">
            <span className="summary-count">{upcomingCnt}</span>
            <span className="summary-label">Upcoming</span>
          </div>
        </div>
        <div className={`summary-card backnone completed ${statusFilter === 'Completed' ? 'active' : ''}`} onClick={() => setStatusFilter(statusFilter === 'Completed' ? '' : 'Completed')}>
          <HiOutlineCheckCircle size={22} />
          <div className="summary-info">
            <span className="summary-count">{completedCnt}</span>
            <span className="summary-label">Completed</span>
          </div>
        </div>
      </div>

      <div className="tasks-layout">
        {/* Left Side: Tasks List */}
        <div className="tasks-list-panel">
          <div className="search-bar">
            <div className="search-input-wrapper">
              <HiOutlineMagnifyingGlass className="search-icon" />
              <input
                type="text"
                placeholder="Search tasks or machines..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-dropdowns">
              <select
                value={plantFilter}
                onChange={(e) => {
                  setPlantFilter(e.target.value);
                }}
                className="filter-select"
              >
                <option value="">All Plants</option>
                {plants.map(p => (
                  <option key={p.plantId} value={p.plantId}>{p.plantName}</option>
                ))}
              </select>

              <select
                value={lineFilter}
                onChange={(e) => setLineFilter(e.target.value)}
                className="filter-select"
                disabled={!plantFilter}
              >
                <option value="">All Lines</option>
                {lines.map(l => (
                  <option key={l.lineId} value={l.lineId}>{l.lineName}</option>
                ))}
              </select>

              <select
                value={frequencyFilter}
                onChange={(e) => setFrequencyFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">All Frequencies</option>
                {uniqueFrequencies.map(freq => (
                  <option key={freq} value={freq}>{freq}</option>
                ))}
              </select>

              {/* Simplified Date Filter */}
              <select
                value={dateFilterMode === 'specific' ? specificDate : dateFilterMode}
                onChange={(e) => {
                  const val = e.target.value;
                  setDateFilterMode(val);
                }}
                className="filter-select"
              >
                <option value="all">All Status</option>
                <option value="today">Due Today</option>
                <option value="overdue">Overdue</option>
                <option value="upcoming">Upcoming</option>
              </select>
            </div>
          </div>

          <div className="tasks-scroll-area">
            {filteredTasks.length === 0 ? (
              <div className="empty-tasks">
                <HiOutlineInformationCircle size={40} />
                <p>{statusFilter ? `No ${statusFilter.toLowerCase()} tasks` : 'No tasks found'}</p>
              </div>
            ) : (
              filteredTasks.map((t, idx) => {
                const config = STATUS_CONFIG[t.status] || STATUS_CONFIG.Due;
                const StatusIcon = config.icon;
                const isSelected = selectedTask &&
                  selectedTask.standardId === t.standardId &&
                  selectedTask.dueDate === t.dueDate;
                return (
                  <div
                    key={`${t.standardId}-${t.dueDate}-${idx}`}
                    className={`task-card ${isSelected ? 'selected' : ''} ${config.className}`}
                    onClick={() => handleSelectTask(t)}
                  >
                    <div className="task-card-header-row">
                      <span className="task-code-box">{t.standardCode}</span>
                      <div className="task-header-meta">
                        <span className="task-date-box">
                          <HiOutlineCalendarDays size={12} />
                          {formatDate(t.dueDate)}
                        </span>
                        <div className={`status-badge-handmade small ${config.className}`}>
                          <StatusIcon size={12} />
                          {config.label}
                        </div>
                      </div>
                    </div>

                    <div className="task-body-row">
                      <h3 className="task-name-large">Task : {t.standardName}</h3>
                    </div>

                    <div className="task-footer-row">
                      <span className="machine-label">Machine: </span>
                      <span className="machine-value">
                        {t.machineName || 'Unknown Machine'}
                      </span>
                    </div>

                    <HiOutlineChevronRight className="arrow-icon-absolute" />
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Side: Task Detail & Form */}
        <div className="task-detail-panel">
          {selectedTask ? (
            <div className="detail-content">
              {(() => {
                const config = STATUS_CONFIG[selectedTask.status] || STATUS_CONFIG.Scheduled;
                const StatusIcon = config.icon;
                return (
                  <div className="detail-header">
                    <div className="detail-header-top">
                      <div className="status-title-group">
                        {searchParams.get('date') && (
                          <button
                            className="back-to-calendar-btn"
                            onClick={() => navigate('/home/pm/calendar')}
                            title="Back to Calendar"
                          >
                            <HiOutlineArrowLeft />
                          </button>
                        )}
                        <span className={`status-badge-handmade large ${config.className}`}>
                          <StatusIcon size={16} />
                          {config.label}
                        </span>
                        <h2>Task : {selectedTask.standardName}</h2>

                        {selectedTask.status === 'Completed' && !isEditing && taskDateStatus !== 'future' && (
                          <button
                            onClick={handleEditTask}
                            className="premium-edit-btn"
                            title="Edit Task Details"
                          >
                            <HiOutlinePencilSquare />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="detail-header-meta">
                      <span className="meta-item">
                        <HiOutlineWrench size={16} />
                        <strong>Machine:</strong> {selectedTask.machineName}
                      </span>
                      <span className="meta-item">
                        <HiOutlineClock size={16} />
                        <strong>Frequency:</strong> {selectedTask.frequency}
                      </span>
                      <span className="meta-item">
                        <HiOutlineCalendarDays size={16} />
                        <strong>Due Date:</strong> {formatDate(selectedTask.dueDate)}
                      </span>
                      <span className="meta-item">
                        <HiOutlineClock size={16} />
                        <strong>Duration:</strong> {selectedTask.minutes ? `${selectedTask.minutes} mins` : '-'}
                      </span>
                      <span className="meta-item">
                        <HiOutlinePower size={16} />
                        <strong>Shutdown Req:</strong> {selectedTask.isMachineShutdownRequired ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                );
              })()}

              <div className="info-grid three-cols">
                <div className="info-box">
                  <label><HiOutlineInformationCircle /> Why (Purpose)</label>
                  <p>{selectedTask.why || 'No purpose defined'}</p>
                </div>
                <div className="info-box">
                  <label><HiOutlineCheckCircle /> How (Method)</label>
                  <p>{selectedTask.how || 'No method defined'}</p>
                </div>
                {selectedTask.standardImage && (
                  <div className="info-box reference-box">
                    <label><HiOutlinePhoto /> Reference Image</label>
                    <div className="standard-ref-thumb" onClick={() => setPreviewImage(selectedTask.standardImage)}>
                      <img src={selectedTask.standardImage} alt="Reference" />
                      <div className="zoom-overlay">Click to enlarge</div>
                    </div>
                  </div>
                )}
              </div>

              {selectedTask.status !== 'Completed' && taskDateStatus === 'past' ? (
                <div className="missed-task-notice">
                  <HiOutlineExclamationTriangle size={32} />
                  <h3>Missed Task</h3>
                  <p>
                    This task was scheduled for <strong>{new Date(selectedTask.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong> and was not completed on time.
                  </p>
                  <p className="missed-policy">
                    ⛔ As per policy, missed PM tasks <strong>cannot be logged retrospectively</strong>.
                    Please notify your supervisor to raise a corrective action.
                  </p>
                </div>
              ) : selectedTask.status !== 'Completed' && taskDateStatus === 'future' ? (
                <div className="future-task-notice">
                  <HiOutlineCalendarDays size={32} />
                  <h3>Upcoming Task</h3>
                  <p>
                    This task is scheduled for <strong>{new Date(selectedTask.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>.
                  </p>
                  <p className="missed-policy">
                    ⏳ PM tasks can only be executed on their scheduled date.
                  </p>
                </div>
              ) : (selectedTask.status !== 'Completed' && taskDateStatus === 'today') || (isEditing && taskDateStatus !== 'future') ? (
                <form className="update-form" onSubmit={handleSubmit}>
                  <h3>
                    <HiOutlinePencilSquare /> {isEditing ? 'Edit Execution Details' : 'Record Execution Details'}
                  </h3>

                  <div className="form-row two-col" style={{ marginBottom: '16px' }}>
                    <div className="form-group">
                      <label>Start Time</label>
                      <input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="time-input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>End Time</label>
                      <input
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="time-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Findings / Observations</label>
                    <textarea
                      rows={3}
                      placeholder="Describe what you found..."
                      value={finding}
                      onChange={(e) => setFinding(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Action Taken</label>
                    <textarea
                      rows={2}
                      placeholder="What did you do to fix it? (if anything)"
                      value={actionTaken}
                      onChange={(e) => setActionTaken(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Spare Parts Consumed</label>
                    <div className="spare-parts-input-group">
                      <div className="select-wrapper">
                        <select
                          value={selectedPartId}
                          onChange={(e) => setSelectedPartId(e.target.value)}
                        >
                          <option value="">-- Select Spare Part --</option>
                          {availableParts.map(p => (
                            <option key={p.sparePartId} value={p.sparePartId}>
                              {p.partName} {p.partNumber ? `(${p.partNumber})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder="Qty"
                        value={partQuantity}
                        onChange={(e) => setPartQuantity(e.target.value)}
                        className="qty-input"
                      />
                      <button
                        type="button"
                        className="add-part-btn"
                        onClick={handleAddPart}
                      >
                        Add
                      </button>
                    </div>
                    {consumedParts.length > 0 && (
                      <div className="consumed-parts-list">
                        {consumedParts.map((cp, idx) => (
                          <div key={idx} className="consumed-part-item">
                            <span className="part-name">{cp.partName}</span>
                            <div className="part-meta">
                              <span className="part-qty">Qty: {cp.quantity}</span>
                              <button type="button" onClick={() => removeConsumedPart(idx)} className="remove-part-icon">
                                <HiOutlineTrash />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Upload Execution Evidence (Completion Photos)</label>
                    <label className="upload-area">
                      <input type="file" accept="image/*,video/*" onChange={handleFileChange} />
                      <div className="upload-icon-box">
                        <HiOutlinePhoto size={24} />
                      </div>
                      <span className="upload-text">Click to upload completion evidence</span>
                      <span className="upload-hint">Images or videos up to 10MB</span>
                    </label>

                    {evidenceFile && (
                      <div className="selected-file-indicator">
                        <HiOutlineCheckCircle />
                        <span>Selected: {evidenceFile.name}</span>
                        <button
                          type="button"
                          style={{ marginLeft: '10px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: '0' }}
                          onClick={removeFile}
                        >
                          <HiOutlineTrash size={16} />
                        </button>
                      </div>
                    )}

                    {evidencePreview && (
                      <div className="preview-container" style={{ marginTop: '12px' }}>
                        <img src={evidencePreview} alt="Preview" className="preview-image" />
                      </div>
                    )}
                  </div>
                  <div className="form-group" style={{ marginTop: '24px' }}>
                    <button type="submit" className="submit-btn" disabled={submitting}>
                      {submitting ? 'Processing...' : (isEditing ? 'Update Task' : 'Complete Task')}
                    </button>
                    {isEditing && (
                      <button
                        type="button"
                        className="cancel-btn"
                        onClick={() => setIsEditing(false)}
                      >
                        Cancel Edit
                      </button>
                    )}
                  </div>
                </form>
              ) : (
                <div className="completed-info">
                  <h3 style={{ marginBottom: '16px' }}><HiOutlineCheckCircle /> Task Completed</h3>


                  <div className="form-row two-col" style={{ marginBottom: '16px', background: '#f8fafc', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0; border-color: var(--border-primary)' }}>
                    <div className="completed-detail">
                      <label>Started At:</label>
                      <p style={{ fontWeight: '600', color: '#0f172a' }}>{selectedTask.startTime ? new Date(selectedTask.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</p>
                    </div>
                    <div className="completed-detail">
                      <label>Ended At:</label>
                      <p style={{ fontWeight: '600', color: '#0f172a' }}>{selectedTask.endTime ? new Date(selectedTask.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</p>
                    </div>
                  </div>

                  <div className="form-row two-col" style={{ marginBottom: '16px', background: '#f8fafc', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0; border-color: var(--border-primary)' }}>
                    <div className="completed-detail">
                      <label>Total Minutes:</label>
                      <p style={{ fontWeight: '600', color: '#0f172a' }}>{selectedTask.minutes ? `${selectedTask.minutes} mins` : '-'}</p>
                    </div>
                    <div className="completed-detail">
                      <label>Machine Shutdown:</label>
                      <p style={{ fontWeight: '600', color: selectedTask?.isMachineShutdownRequired ? '#ef4444' : SEMANTIC_SUCCESS_MID }}>
                        {selectedTask.isMachineShutdownRequired ? 'Yes (Required)' : 'No'}
                      </p>
                    </div>
                  </div>

                  {selectedTask.finding && (
                    <div className="completed-detail">
                      <label>Finding:</label>
                      <p>{selectedTask.finding}</p>
                    </div>
                  )}
                  {selectedTask.actionTaken && (
                    <div className="completed-detail">
                      <label>Action Taken:</label>
                      <p>{selectedTask.actionTaken}</p>
                    </div>
                  )}
                  {selectedTask.evidenceUrl && (
                    <div className="completed-detail">
                      <label>Evidence View:</label>
                      <div className="evidence-preview" onClick={() => setPreviewImage(selectedTask.evidenceUrl)}>
                        <img src={selectedTask.evidenceUrl} alt="Evidence" />
                        <div className="zoom-overlay">Click to enlarge</div>
                      </div>
                    </div>
                  )}
                  {selectedTask.sparePartsJson && (
                    <div className="completed-detail">
                      <label>Spare Parts Used:</label>
                      <div className="consumed-parts-list" style={{ marginTop: '8px' }}>
                        {JSON.parse(selectedTask.sparePartsJson).map((cp, idx) => (
                          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: '#f8fafc', borderRadius: '6px', marginBottom: '6px', border: '1px solid #e2e8f0; border-color: var(--border-primary)' }}>
                            <span>{cp.PartName || cp.partName}</span>
                            <span style={{ fontWeight: 500 }}>Qty: {cp.Quantity || cp.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="history-section">
                <h3><HiOutlineClock /> Recent History</h3>
                {loadingHistory ? (
                  <p>Loading history...</p>
                ) : history.length === 0 ? (
                  <p className="no-history">No previous records found</p>
                ) : (
                  <div className="history-list">
                    {history.slice(0, 5).map(h => (
                      <div key={h.id} className="history-item">
                        <div className="history-date">
                          {new Date(h.transactionDate).toLocaleDateString()}
                        </div>
                        <div className="history-info">
                          <p><strong>Finding:</strong> {h.finding}</p>
                          {h.actionTaken && <p><strong>Action:</strong> {h.actionTaken}</p>}
                          {h.sparePartsJson && (
                            <div style={{ marginTop: '4px', fontSize: '0.9em', color: '#64748b' }}>
                              <strong>Parts used: </strong>
                              {JSON.parse(h.sparePartsJson).map((cp, idx) => (
                                <span key={idx}>
                                  {cp.PartName || cp.partName} (x{cp.Quantity || cp.quantity}){idx < JSON.parse(h.sparePartsJson).length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </div>
                          )}
                          {h.evidenceUrl && (
                            <div className="history-evidence">
                              <img src={h.evidenceUrl} alt="History Evidence" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="no-selection">
              <HiOutlineClipboardDocumentList size={60} />
              <p>Select a task from the list to view details and record findings</p>
            </div>
          )}
        </div>
      </div>

      <SharedModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        title="Image Preview"
        customWidth="auto"
      >
        <div className="shared-modal-body" style={{ padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#000' }}>
            <img 
                src={previewImage} 
                alt="Full Preview" 
                style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
            />
        </div>
      </SharedModal>
    </div>
  );
};

export default OperatorPmTasks;
